import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, XCircle, CheckCircle, Loader2, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const weekdayOptions = () => {
  const out = [];
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
  for (let i = 1; i < 60 && out.length < 30; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    if (d.getDay() !== 0 && d.getDay() !== 6) {
      out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
  }
  return out;
};

const fmtDate = (s) => new Date(s + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const Reschedule = ({ booking, token, onDone }) => {
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!date) return;
    let ignore = false;
    setLoading(true); setTime(''); setError(''); setSlots([]);
    fetch(`${API_URL}/api/manage/${token}/slots?date=${date}`)
      .then(async r => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        if (!ignore) setSlots(d.slots || []);
      })
      .catch(() => { if (!ignore) setError('Could not load times. Please try again.'); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [date, token]);

  const submit = async () => {
    setSaving(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/manage/${token}/reschedule`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ date, time }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.detail || 'Could not reschedule'); return; }
      onDone(`Your appointment is now on ${fmtDate(d.date)} at ${d.time} (Ibiza time). We've emailed you the new details.`);
    } catch { setError('Connection problem. Please try again.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-[#e2e8f0] bg-white p-5 space-y-4" data-testid="reschedule-section">
      <h3 className="font-semibold text-[#0f172a] flex items-center gap-2"><Calendar size={18} /> Choose a new time</h3>
      <select data-testid="reschedule-date" value={date} onChange={e => setDate(e.target.value)}
        className="w-full p-3 border border-[#e2e8f0] bg-white focus:outline-none focus:border-[#b8860b]">
        <option value="">Select a date...</option>
        {weekdayOptions().map(d => <option key={d} value={d}>{fmtDate(d)}</option>)}
      </select>
      {loading && <Loader2 className="animate-spin text-[#b8860b]" size={20} />}
      {date && !loading && !error && (slots.length ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="reschedule-slots">
          {slots.map(s => (
            <button key={s} data-testid={`reschedule-slot-${s}`} onClick={() => setTime(s)}
              className={`py-2 text-sm border transition-colors ${time === s ? 'bg-[#0f172a] text-white border-[#0f172a]' : 'border-[#e2e8f0] hover:border-[#b8860b]'}`}>{s}</button>
          ))}
        </div>
      ) : <p className="text-sm text-[#64748b]">No open times on this date.</p>)}
      {error && <p className="text-sm text-red-600" data-testid="reschedule-error">{error}</p>}
      <button data-testid="reschedule-confirm-btn" disabled={!time || saving} onClick={submit}
        className="px-6 py-3 bg-[#0f172a] text-white font-medium hover:bg-[#b8860b] transition-colors disabled:bg-[#e2e8f0] disabled:text-[#94a3b8]">
        {saving ? 'Saving...' : 'Confirm new time'}
      </button>
    </div>
  );
};

export default function ManageBookingPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/manage/${token}`);
      if (!r.ok) { setError('We could not find this booking. Please check the link in your email.'); return; }
      setBooking(await r.json());
    } catch { setError('Connection problem. Please refresh the page.'); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    if (!window.confirm('Cancel this appointment? You will receive a full refund.')) return;
    setCancelling(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/manage/${token}/cancel`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) { setError(d.detail || 'Could not cancel'); return; }
      setDone(d.payment_status === 'refunded'
        ? 'Your appointment has been cancelled and a full refund has been issued. It usually appears within 5–10 business days.'
        : 'Your appointment has been cancelled. Our team will process your full refund within 2 business days.');
      load();
    } catch { setError('Connection problem. Please try again.'); }
    finally { setCancelling(false); }
  };

  const service = booking?.service === 'home_visit' ? 'Home Visit' : 'Telemedicine';

  return (
    <div className="min-h-screen bg-[#f8fafc] px-4 py-12" data-testid="manage-booking-page">
      <div className="max-w-xl mx-auto space-y-6">
        <button onClick={() => navigate('/')} className="text-sm text-[#64748b] hover:text-[#0f172a]" data-testid="manage-back-home">← Dr. Carlos Castellon</button>
        <h1 className="text-3xl font-semibold text-[#0f172a]">Manage your booking</h1>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 flex gap-2" data-testid="manage-error"><XCircle size={18} />{error}</div>}
        {done && <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 flex gap-2" data-testid="manage-success"><CheckCircle size={18} />{done}</div>}
        {!booking && !error && <Loader2 className="animate-spin text-[#b8860b]" />}
        {booking && (
          <>
            <div className="border border-[#e2e8f0] bg-white p-5 space-y-2" data-testid="manage-booking-details">
              <p className="text-xs uppercase tracking-wider text-[#b8860b]">Booking {booking.booking_id}</p>
              <p className="text-lg font-semibold text-[#0f172a]">{service}</p>
              <p className="flex items-center gap-2 text-[#475569]"><Calendar size={16} />{fmtDate(booking.date)}</p>
              <p className="flex items-center gap-2 text-[#475569]"><Clock size={16} />{booking.time} (Ibiza time)</p>
              <p className="text-sm" data-testid="manage-status">
                <span className={`px-2 py-0.5 text-xs uppercase tracking-wider ${booking.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{booking.status}</span>
              </p>
            </div>
            {booking.status === 'cancelled' && (
              <div className="border-l-4 border-[#b8860b] bg-white p-4 text-[#475569] space-y-3" data-testid="manage-cancelled-info">
                {!done && <p>This appointment has been cancelled{booking.payment_status === 'refunded' ? ' and your payment has been refunded' : ['refunding', 'refund_failed'].includes(booking.payment_status) ? ' and your payment is being refunded' : ''}.</p>}
                <button onClick={() => navigate('/book')} data-testid="manage-book-again"
                  className="px-5 py-2 bg-[#0f172a] text-white hover:bg-[#b8860b] transition-colors">Book a new appointment</button>
              </div>
            )}
            {booking.status === 'confirmed' && booking.can_change && !done && (
              <>
                <Reschedule booking={booking} token={token} onDone={(msg) => { setDone(msg); load(); }} />
                <button data-testid="manage-cancel-btn" onClick={cancel} disabled={cancelling}
                  className="w-full py-3 border border-red-300 text-red-600 hover:bg-red-50 transition-colors">
                  {cancelling ? 'Cancelling...' : 'Cancel appointment & get a full refund'}
                </button>
              </>
            )}
            {booking.status === 'confirmed' && !booking.can_change && (
              <div className="border-l-4 border-[#b8860b] bg-white p-4 text-[#475569]" data-testid="manage-locked">
                Your appointment is less than 24 hours away, so it can no longer be changed online.
                <a href="https://wa.me/34635794368" target="_blank" rel="noopener noreferrer" data-testid="manage-whatsapp"
                  className="mt-3 flex items-center gap-2 text-[#0f172a] font-medium hover:text-[#25d366]"><MessageCircle size={18} /> Contact us on WhatsApp</a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
